import { useState } from "react";
import { toast } from "sonner";
import { Reveal, Chapter } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRITERIA = [
  "Operational Competence",
  "Leadership Ability",
  "Cultural Compatibility",
  "Commercial Awareness",
  "Guest Experience Mindset",
  "Career Stability",
  "International Exposure",
];

const FIELDS = [
  { name: "name", label: "Name", required: true },
  { name: "company", label: "Company" },
  { name: "position", label: "Position" },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "Phone" },
  { name: "property_name", label: "Property / Restaurant Name" },
  { name: "location", label: "Location" },
  { name: "positions_required", label: "Positions Required" },
];

const Employers = () => {
  const [form, setForm] = useState({});
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success("Enquiry sent. We will be in touch shortly.");
      setForm({});
      e.target.reset();
    } catch {
      toast.error("Something went wrong. Please email info@br-hospitality.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="employers" data-testid="employers-section" className="bg-ivory py-28 lg:py-40 hairline">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-24">
          <div className="lg:col-span-5">
            <Reveal>
              <Chapter number="09" label="For Employers" />
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl sm:text-5xl font-light leading-[1.08] text-ink">
                Exceptional Brands Require{" "}
                <span className="italic text-bronze">Exceptional People</span>
              </h2>
              <p className="mt-7 text-sm text-ink/60 leading-relaxed">
                Whether you are replacing a key leader, strengthening an existing team or preparing
                to launch a new concept, BR Hospitality provides targeted recruitment solutions
                built around the identity of your operation.
              </p>
              <p className="mt-4 text-sm text-ink/60 leading-relaxed">
                Our process focuses on quality rather than volume. We evaluate candidates based on:
              </p>
              <ul className="mt-6 space-y-2.5">
                {CRITERIA.map((c) => (
                  <li key={c} className="flex items-center gap-3 text-sm text-ink/75">
                    <span className="w-1 h-1 bg-bronze rounded-full shrink-0" />
                    {c}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <form
                data-testid="employer-enquiry-form"
                onSubmit={submit}
                className="border border-ink/10 p-8 lg:p-12 bg-ivory"
              >
                <p className="eyebrow mb-10">Start a Talent Search</p>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                  {FIELDS.map((f) => (
                    <div key={f.name}>
                      <label className="field-label" htmlFor={`emp-${f.name}`}>
                        {f.label}
                        {f.required ? " *" : ""}
                      </label>
                      <input
                        id={`emp-${f.name}`}
                        data-testid={`employer-field-${f.name.replace(/_/g, "-")}`}
                        type={f.type || "text"}
                        required={f.required}
                        onChange={set(f.name)}
                        className="field-input"
                      />
                    </div>
                  ))}
                  <div className="sm:col-span-2">
                    <label className="field-label" htmlFor="emp-message">
                      Message
                    </label>
                    <textarea
                      id="emp-message"
                      data-testid="employer-field-message"
                      rows={4}
                      onChange={set("message")}
                      className="field-input resize-none"
                    />
                  </div>
                </div>
                <button
                  data-testid="employer-submit-button"
                  type="submit"
                  disabled={sending}
                  className="mt-10 w-full sm:w-auto text-[11px] tracking-[0.25em] uppercase px-10 py-4 bg-ink text-ivory hover:bg-bronze hover:text-ink transition-colors duration-500 disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Send Enquiry"}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Employers;
